import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EyeIcon,
  NoSymbolIcon,
  PhotoIcon,
  RocketLaunchIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Tooltip } from "react-tooltip";

import DashboardLayout from "../../components/DashboardLayout";
import AgentLog from "../../components/common/AgentLog";
import PremiumModelPicker from "../../components/common/PremiumModelPicker";
import StatusBadge from "../../components/common/StatusBadge";
import SummaryText from "../../components/common/SummaryText";
import VoiceInputButton from "../../components/common/VoiceInputButton";
import WeaveLoader from "../../components/common/WeaveLoader";
import { useToast } from "../../contexts/ToastContext";
import { setCurrentProject } from "../../redux/project/projectSlice";
import { changeRequestsService } from "../../services/changeRequests";
import { deploymentsService } from "../../services/deployments";
import { projectsService } from "../../services/projects";
import { seoService } from "../../services/seo";
import { getApiErrorMessage } from "../../utils/axios";
import {
  AGENT_LABELS_LONG,
  formatCost,
  formatDurationSeconds,
  formatModel,
  formatRunLabel,
  formatTokens,
  formatTotalDuration,
  runDurationSeconds,
} from "../../utils/agentLog";
import { handleBillingError } from "../../utils/billing";
import { clearChangeDraft, persistChangeDraft, useChangeDraft } from "../../utils/changeDraft";
import { refreshSidebarBadges } from "../../utils/sidebarBadges";
import { sitePath } from "../../utils/urls";

const ACTIVE_CHANGE_STATUSES = new Set(["queued", "running"]);
const PAUSED_CHANGE_STATUSES = new Set(["failed", "canceled"]);
const ACTIVE_DEPLOY_STATUSES = new Set(["building"]);
const PENDING_PREFIX = "pending-";
const TOAST_MS = 5000;

const hasActiveAgentRun = (change) => (
  (change.agent_runs || []).some((run) => run.status === "running" || run.status === "pending")
);

const isQueuedChange = (change) => (
  change.status === "queued" && !hasActiveAgentRun(change)
);

const isInProgressChange = (change) => (
  ACTIVE_CHANGE_STATUSES.has(change.status)
  || PAUSED_CHANGE_STATUSES.has(change.status)
  || String(change.hash).startsWith(PENDING_PREFIX)
  || hasActiveAgentRun(change)
);

const isDraftChange = (change) => change.status === "completed";

const isOpenDraftChange = (change) => (
  change.status !== "rejected" && change.status !== "published"
);

const changeSortKey = (change) => [
  new Date(change.create_date).getTime(),
  change.id || 0,
];

const isNewerThan = (other, change) => {
  const [aTime, aId] = changeSortKey(other);
  const [bTime, bId] = changeSortKey(change);
  return aTime > bTime || (aTime === bTime && aId > bId);
};

const ACTION_ICON = "h-3.5 w-3.5 shrink-0";
const ACTION_SECONDARY = "inline-flex shrink-0 items-center gap-1 rounded-md bg-theme-secondary px-2.5 py-1 text-xs font-semibold";
const ACTION_PRIMARY = "inline-flex shrink-0 items-center gap-1 rounded-md bg-theme-primary px-2.5 py-1 text-xs font-semibold text-theme-button";

const Changes = ({ currentUser }) => {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project.currentProject);
  const location = useLocation();
  const navigate = useNavigate();
  const { success, error, warning } = useToast();
  const [changes, setChanges] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [description, setDescription] = useState("");
  const [modelOverride, setModelOverride] = useState(project?.model_override || "");
  const [suggestionHashes, setSuggestionHashes] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const publishBaselineRef = useRef([]);
  const [submitting, setSubmitting] = useState(false);
  const [focusPublish, setFocusPublish] = useState(false);
  const showNew = /\/changes\/new\/?$/.test(location.pathname);
  const showBuildCost = Boolean(currentUser?.features?.build_cost);

  useChangeDraft(project?.hash, description, setDescription);

  useEffect(() => {
    if (showNew) setModelOverride(project?.model_override || "");
  }, [showNew, project?.hash, project?.model_override]);

  // Prefill new-change modal from ?description= or ?suggestions=hash1,hash2
  // Runs after draft restore so URL/suggestion text wins when present.
  useEffect(() => {
    if (!showNew || !project) return undefined;
    const params = new URLSearchParams(location.search);
    const text = params.get("description") || params.get("text") || "";
    const hashes = (params.get("suggestions") || params.get("suggestion") || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    setSuggestionHashes(hashes);
    if (text) {
      setDescription(text);
      return undefined;
    }
    if (!hashes.length) return undefined;
    let cancelled = false;
    seoService.listSuggestions(project.hash, { status: "open" }, (response) => {
      if (cancelled) return;
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : data?.suggestions || data?.results || [];
      const matched = hashes
        .map((hash) => list.find((item) => item.hash === hash))
        .filter(Boolean);
      if (matched.length) setDescription(promptFromSuggestions(matched));
    });
    return () => {
      cancelled = true;
    };
  }, [showNew, project, location.search]);

  const projectHash = project?.hash;
  const loadChanges = useCallback(() => {
    if (!projectHash) return;
    changeRequestsService.list(projectHash, {}, (response) => {
      const incoming = Array.isArray(response.data) ? response.data : response.data.results;
      setChanges((current) => {
        const pending = current.filter((item) => String(item.hash).startsWith(PENDING_PREFIX));
        const stillPending = pending.filter(
          (item) => !incoming.some((row) => row.description === item.description),
        );
        return stillPending.length ? [...incoming, ...stillPending] : incoming;
      });
      setLoaded(true);
    }, () => {
      setLoaded(true);
    });
  }, [projectHash]);

  const unpublishedCompleted = useMemo(
    () => changes.filter((change) => change.status === "completed"),
    [changes],
  );

  const hasActiveChanges = useMemo(
    () => changes.some(
      (change) => (
        isInProgressChange(change)
        || isQueuedChange(change)
        || (change.deployments || []).some((d) => ACTIVE_DEPLOY_STATUSES.has(d.status))
      ),
    ) || publishing,
    [changes, publishing],
  );

  useEffect(() => {
    setLoaded(false);
    setChanges([]);
    loadChanges();
  }, [loadChanges]);

  useEffect(() => {
    if (!hasActiveChanges) return undefined;
    const timer = setInterval(loadChanges, 5000);
    return () => clearInterval(timer);
  }, [hasActiveChanges, loadChanges]);

  const hasActiveSitebuilder = useMemo(
    () => changes.some((change) => (
      change.agent_type === "sitebuilder" && isInProgressChange(change)
    )),
    [changes],
  );

  useEffect(() => {
    if (!projectHash || !hasActiveSitebuilder) return undefined;
    const refresh = () => {
      projectsService.getProject(projectHash, (response) => {
        dispatch(setCurrentProject(response.data));
      });
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [projectHash, hasActiveSitebuilder, dispatch]);

  useEffect(() => {
    if (!publishing) return;
    const baseline = publishBaselineRef.current;
    if (!baseline.length) return;
    const byHash = Object.fromEntries(changes.map((change) => [change.hash, change]));
    const deployments = baseline.flatMap((hash) => byHash[hash]?.deployments || []);
    const building = deployments.some((deployment) => deployment.status === "building");
    const failed = deployments.some((deployment) => deployment.status === "failed");
    if (failed && !building) {
      setPublishing(false);
      publishBaselineRef.current = [];
      error("Publish failed", TOAST_MS);
      return;
    }
    const stillPending = baseline.some((hash) => byHash[hash]?.status === "completed");
    if (stillPending || building) return;
    const published = baseline.every((hash) => (
      !byHash[hash] || byHash[hash].status === "published"
    ));
    if (!published) return;
    setPublishing(false);
    publishBaselineRef.current = [];
    success("Published to live", 3000);
  }, [publishing, changes, success, error]);

  useEffect(() => {
    if (!publishing) return undefined;
    const timer = setTimeout(() => setPublishing(false), 180000);
    return () => clearTimeout(timer);
  }, [publishing]);

  const sortedChanges = useMemo(
    () => changes.slice().sort((a, b) => {
      const [aTime, aId] = changeSortKey(a);
      const [bTime, bId] = changeSortKey(b);
      return bTime - aTime || bId - aId;
    }),
    [changes],
  );

  // Running/paused first, then queued oldest → newest (queue order).
  const inProgressChanges = useMemo(() => {
    const items = sortedChanges.filter(isInProgressChange);
    return items.slice().sort((a, b) => {
      const aQueued = isQueuedChange(a) ? 1 : 0;
      const bQueued = isQueuedChange(b) ? 1 : 0;
      if (aQueued !== bQueued) return aQueued - bQueued;
      const [aTime, aId] = changeSortKey(a);
      const [bTime, bId] = changeSortKey(b);
      return aQueued ? aTime - bTime || aId - bId : bTime - aTime || bId - aId;
    });
  }, [sortedChanges]);

  const draftChanges = useMemo(
    () => sortedChanges.filter(isDraftChange),
    [sortedChanges],
  );

  const draftPreviewHref = project ? sitePath(project, "preview/?env=draft") : null;

  const publishingDeployment = useMemo(() => {
    for (const change of draftChanges) {
      const active = (change.deployments || []).find(
        (deployment) => deployment.status === "building" || deployment.status === "failed",
      );
      if (active) return active;
    }
    return null;
  }, [draftChanges]);

  const liveGroups = useMemo(() => {
    const groups = new Map();
    sortedChanges.filter((change) => change.status === "published").forEach((change) => {
      const deployment = (change.deployments || [])[0];
      const key = change.deployment_hash || deployment?.hash || change.published_at || change.hash;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          deployment: deployment || {
            hash: key,
            status: "live",
            changelog: change.description,
            create_date: change.published_at || change.create_date,
            resolved_at: change.published_at || change.create_date,
          },
          changes: [],
        });
      }
      groups.get(key).changes.push(change);
    });
    return [...groups.values()].sort((a, b) => {
      const aTime = new Date(
        a.deployment?.resolved_at || a.deployment?.create_date || a.changes[0]?.published_at || 0,
      ).getTime();
      const bTime = new Date(
        b.deployment?.resolved_at || b.deployment?.create_date || b.changes[0]?.published_at || 0,
      ).getTime();
      return bTime - aTime;
    });
  }, [sortedChanges]);

  const draftTip = useMemo(
    () => sortedChanges.find(isOpenDraftChange) || null,
    [sortedChanges],
  );

  const canActOnPaused = useCallback((change) => {
    if (!PAUSED_CHANGE_STATUSES.has(change.status)) return false;
    const newer = sortedChanges.filter(
      (other) => isOpenDraftChange(other) && isNewerThan(other, change),
    );
    return newer.every((other) => other.status === "queued" && !hasActiveAgentRun(other));
  }, [sortedChanges]);

  const canRejectCompleted = useCallback((change) => (
    change.status === "completed"
    && change.agent_type !== "sitebuilder"
    && draftTip?.hash === change.hash
  ), [draftTip]);

  const latestPublishable = useMemo(
    () => sortedChanges.find((change) => change.status === "completed") || null,
    [sortedChanges],
  );

  const isDeploying = useMemo(
    () => publishing || changes.some((change) => (
      (change.deployments || []).some((d) => ACTIVE_DEPLOY_STATUSES.has(d.status))
      || (
        change.environment_status === "processing"
        && change.environment_status_message === "Publishing to live"
      )
    )),
    [changes, publishing],
  );

  const canPublishLatest = useMemo(() => (
    Boolean(latestPublishable) && !isDeploying
  ), [latestPublishable, isDeploying]);

  const toastError = useCallback((message, duration, options) => (
    error(message, duration === undefined ? TOAST_MS : duration, options)
  ), [error]);

  const submit = (event) => {
    event.preventDefault();
    if (!description.trim() || submitting) return;
    const text = description.trim();
    const selectedModel = modelOverride;
    const linkedSuggestions = suggestionHashes;
    const queueBehind = changes.some(
      (change) => ACTIVE_CHANGE_STATUSES.has(change.status) || hasActiveAgentRun(change),
    );
    persistChangeDraft(project.hash, text);
    setSubmitting(true);
    changeRequestsService.create(
      project.hash,
      text,
      "form",
      (response) => {
        clearChangeDraft(project.hash);
        setDescription("");
        setSuggestionHashes([]);
        setModelOverride(project?.model_override || "");
        setSubmitting(false);
        navigate(sitePath(project, "changes/"));
        const created = response.data;
        setChanges((items) => {
          if (items.some((item) => item.hash === created.hash)) return items;
          return [...items, created];
        });
        if (queueBehind) {
          warning("Queued. This will run after your other changes finish.", TOAST_MS);
        }
        loadChanges();
        refreshSidebarBadges();
      },
      (err) => {
        setSubmitting(false);
        if (handleBillingError(err, toastError)) return;
        toastError(getApiErrorMessage(err, "Could not queue that change"));
      },
      { agent_type: "composite", model_override: selectedModel, suggestions: linkedSuggestions },
    );
  };

  const resolve = (change, action) => {
    if (String(change.hash).startsWith(PENDING_PREFIX)) return;
    const apply = (response) => {
      const updated = response?.data;
      if (updated?.hash) {
        setChanges((items) => items.map((item) => (item.hash === updated.hash ? { ...item, ...updated } : item)));
        return;
      }
      loadChanges();
    };
    const fail = (err) => toastError(getApiErrorMessage(err, "Could not update that change"));
    if (action === "retry") changeRequestsService.retry(project.hash, change.hash, apply, fail);
    else if (action === "cancel") {
      setChanges((items) => items.map((item) => (
        item.hash === change.hash && change.status === "running" ? stoppedChange(item) : item
      )));
      changeRequestsService.cancel(project.hash, change.hash, apply, (err) => {
        loadChanges();
        fail(err);
      });
    } else if (action === "approve") {
      publishBaselineRef.current = unpublishedCompleted.map((item) => item.hash);
      setPublishing(true);
      changeRequestsService.approve(
        project.hash,
        change.hash,
        (response) => {
          const updated = response?.data;
          const building = (updated?.deployments || []).find((d) => d.status === "building");
          if (updated?.hash) {
            // Pin the same deployment onto the whole publish batch for polling.
            setChanges((items) => items.map((item) => {
              if (item.hash === updated.hash) return { ...item, ...updated };
              if (
                building
                && publishBaselineRef.current.includes(item.hash)
                && item.status === "completed"
              ) {
                return { ...item, deployments: [building, ...(item.deployments || [])] };
              }
              return item;
            }));
            if (building?.hash) setFocusPublish(true);
            return;
          }
          apply(response);
        },
        (err) => {
          setPublishing(false);
          publishBaselineRef.current = [];
          fail(err);
        },
      );
    } else if (action === "reject") {
      setChanges((items) => items.map((item) => (
        item.hash === change.hash ? rejectedChange(item) : item
      )));
      changeRequestsService.reject(project.hash, change.hash, apply, (err) => {
        loadChanges();
        fail(err);
      });
    }
  };

  const resolveDeployment = (deployment, action) => {
    if (action === "rollback") deploymentsService.rollback(project.hash, deployment.hash, loadChanges);
    else if (action === "redeploy") {
      deploymentsService.redeploy(project.hash, deployment.hash, (response) => {
        const updated = response?.data;
        if (updated?.hash) {
          setChanges((items) => items.map((item) => {
            if (!(item.deployments || []).some((d) => d.hash === deployment.hash)) return item;
            return { ...item, deployments: [...(item.deployments || []), updated] };
          }));
          return;
        }
        loadChanges();
      });
    } else if (action === "cancel") deploymentsService.cancel(project.hash, deployment.hash, loadChanges);
  };

  return (
    <DashboardLayout title="Changes" currentUser={currentUser} hideDesktopHeader>
      <div className="flex">
        <div className="w-full lg:w-3/4">
        <div className="pt-2 lg:pt-4">
          <ChangeComposer
            description={description}
            modelOverride={modelOverride}
            onChange={setDescription}
            onModelChange={setModelOverride}
            onSubmit={submit}
            autoFocus
            disabled={submitting}
          />
        </div>
        <div>
          <div className="pb-8 pt-8">
            {!loaded ? (
              <div className="flex items-center justify-center py-24">
                <WeaveLoader size={72} />
              </div>
            ) : !sortedChanges.length ? (
              <p className="py-16 text-center text-sm opacity-45">
                No changes yet. Describe one above.
              </p>
            ) : (
              <>
                {inProgressChanges.length > 0 && (
                  <section className="mb-8">
                    <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-theme-secondary-text">
                      In progress
                    </h2>
                    <div className="overflow-hidden rounded-2xl border border-theme-secondary divide-y divide-theme-secondary">
                      {inProgressChanges.map((change) => (
                        <ChangeCard
                          key={change.hash}
                          change={change}
                          onResolve={resolve}
                          onDeploymentAction={resolveDeployment}
                          onChangeUpdated={(updated) => {
                            setChanges((items) => items.map((item) => (
                              item.hash === updated.hash ? { ...item, ...updated } : item
                            )));
                          }}
                          showBuildCost={showBuildCost}
                          showPreview={false}
                          canReject={false}
                          canDiscard={change.agent_type !== "sitebuilder" && canActOnPaused(change)}
                          canRetry={canActOnPaused(change)}
                          listMode
                          grouped
                          startOpen={
                            change.status === "running"
                            || change.status === "failed"
                            || hasActiveAgentRun(change)
                          }
                        />
                      ))}
                    </div>
                  </section>
                )}
                {(draftChanges.length > 0 || latestPublishable) && (
                  <section className="mb-8">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-theme-secondary-text">
                        Ready to publish
                      </h2>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {draftPreviewHref && (
                          <Link to={draftPreviewHref} className={ACTION_SECONDARY}>
                            <EyeIcon className={ACTION_ICON} aria-hidden="true" />
                            Preview
                          </Link>
                        )}
                        {latestPublishable && (
                          canPublishLatest ? (
                            <button
                              type="button"
                              onClick={() => resolve(latestPublishable, "approve")}
                              className={ACTION_PRIMARY}
                            >
                              <RocketLaunchIcon className={ACTION_ICON} aria-hidden="true" />
                              {`Publish ${draftChanges.length} draft change${draftChanges.length === 1 ? "" : "s"}`}
                            </button>
                          ) : (
                            <span className={`${ACTION_PRIMARY} opacity-60`}>
                              <RocketLaunchIcon className={ACTION_ICON} aria-hidden="true" />
                              {isDeploying
                                ? "Publishing"
                                : `Publish ${draftChanges.length} draft change${draftChanges.length === 1 ? "" : "s"}`}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-theme-secondary divide-y divide-theme-secondary">
                      {draftChanges.map((change) => (
                        <ChangeCard
                          key={change.hash}
                          change={change}
                          onResolve={resolve}
                          onDeploymentAction={resolveDeployment}
                          showBuildCost={showBuildCost}
                          canReject={canRejectCompleted(change)}
                          canDiscard={false}
                          canRetry={false}
                          listMode
                          grouped
                          hideDeployments
                        />
                      ))}
                    </div>
                  </section>
                )}
                {(liveGroups.length > 0 || publishingDeployment) && (
                  <LiveChanges
                    groups={liveGroups}
                    publishingDeployment={publishingDeployment}
                    focusPublish={focusPublish}
                    onFocusPublishHandled={() => setFocusPublish(false)}
                    onCancelPublish={(deployment) => resolveDeployment(deployment, "cancel")}
                    renderChange={(change, { startOpen } = {}) => (
                      <ChangeCard
                        change={change}
                        onResolve={resolve}
                        showBuildCost={showBuildCost}
                        showPreview={false}
                        canReject={false}
                        canDiscard={false}
                        canRetry={false}
                        listMode
                        grouped
                        hideDeployments
                        startOpen={Boolean(startOpen)}
                      />
                    )}
                  />
                )}
              </>
            )}
          </div>
        </div>
        </div>
        <div className="hidden lg:block lg:w-1/4" aria-hidden="true" />
      </div>
    </DashboardLayout>
  );
};

const STACK_PEEK = 24;
const STACK_INSET = 10;

const LiveChanges = ({
  groups,
  publishingDeployment,
  focusPublish,
  onFocusPublishHandled,
  onCancelPublish,
  renderChange,
}) => {
  const publishRef = useRef(null);

  useEffect(() => {
    if (!focusPublish || !publishingDeployment) return undefined;
    const timer = setTimeout(() => {
      publishRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      onFocusPublishHandled?.();
    }, 50);
    return () => clearTimeout(timer);
  }, [focusPublish, publishingDeployment, onFocusPublishHandled]);

  return (
    <div>
      <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-theme-secondary-text">
        Published
      </h2>
      <div className="space-y-8">
        {publishingDeployment && (
          <section ref={publishRef}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                {publishingDeployment.status === "building" && (
                  <span className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-primary" />
                )}
                <span className="truncate">
                  {publishingDeployment.status === "failed" ? "Publish failed" : "Publishing"}
                  <span className="font-normal opacity-50">
                    {` · ${formatPublishWhen(publishingDeployment.create_date)}`}
                  </span>
                </span>
              </p>
              {publishingDeployment.status === "building" && onCancelPublish && (
                <button
                  type="button"
                  onClick={() => onCancelPublish(publishingDeployment)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-theme-secondary px-2.5 py-1 text-xs font-semibold"
                >
                  <NoSymbolIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Cancel
                </button>
              )}
            </div>
            <div className="overflow-hidden rounded-xl border border-theme-secondary">
              <AgentLog
                lines={publishingDeployment.deploy_log || []}
                live={publishingDeployment.status === "building"}
              />
              {!(publishingDeployment.deploy_log || []).length && publishingDeployment.status_message && (
                <p className="px-3 py-2 text-sm opacity-60">{publishingDeployment.status_message}</p>
              )}
            </div>
          </section>
        )}
        {groups.map((group, index) => (
          <PublishGroup
            key={group.key}
            group={group}
            isLatest={index === 0}
            renderChange={renderChange}
          />
        ))}
      </div>
    </div>
  );
};

const PublishGroup = ({ group, isLatest = false, renderChange }) => {
  const project = useSelector((state) => state.project.currentProject);
  const changes = group.changes || [];
  const canStack = changes.length > 1;
  const [expanded, setExpanded] = useState(!canStack);
  const [openTop, setOpenTop] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  const expandSection = () => {
    setExpanded(true);
    setOpenTop(true);
  };
  const deployLog = group.deployment?.deploy_log || [];
  const whenLabel = formatPublishWhen(
    group.deployment?.resolved_at
    || group.deployment?.create_date
    || changes[0]?.published_at
    || changes[0]?.create_date,
  );
  const peek = changes.slice(1, 3);
  const peekHeight = peek.length * STACK_PEEK;
  const siteUrl = project?.custom_domain
    ? `https://${project.custom_domain}`
    : (project?.production_url || project?.staging_url || "");
  const previewHref = project ? sitePath(project, "preview/?env=live") : "";
  const headerLinkClass = "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs opacity-50 hover:opacity-100";

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            if (!canStack) return;
            if (expanded) {
              setExpanded(false);
              setOpenTop(false);
            } else {
              expandSection();
            }
          }}
          className={`min-w-0 text-left ${canStack ? "hover:opacity-80" : "cursor-default"}`}
        >
          <p className="truncate text-sm font-semibold">
            {whenLabel || "Published"}
            {changes.length > 1 ? (
              <span className="font-normal opacity-50">{` · ${changes.length} changes`}</span>
            ) : null}
          </p>
        </button>
        <div className="flex shrink-0 items-center gap-0.5">
          {isLatest && siteUrl && (
            <a
              href={siteUrl}
              target="_blank"
              rel="noreferrer"
              className={headerLinkClass}
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
              view
            </a>
          )}
          {isLatest && previewHref && (
            <Link to={previewHref} className={headerLinkClass}>
              <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" />
              preview
            </Link>
          )}
          {deployLog.length > 0 && (
            <button
              type="button"
              onClick={() => setLogsOpen((value) => !value)}
              className={`${headerLinkClass} ${logsOpen ? "bg-theme-secondary opacity-100" : ""}`}
              title="Deploy logs"
            >
              <DocumentTextIcon className="h-3.5 w-3.5" aria-hidden="true" />
              logs
            </button>
          )}
        </div>
      </div>

      {logsOpen && deployLog.length > 0 && (
        <div className="mb-3 overflow-hidden rounded-xl border border-theme-secondary">
          <AgentLog lines={deployLog} compact />
        </div>
      )}

      {!canStack || expanded ? (
        <div className="overflow-hidden rounded-2xl border border-theme-secondary divide-y divide-theme-secondary">
          {changes.map((change, index) => (
            <div key={change.hash}>
              {renderChange(change, { startOpen: index === 0 && openTop })}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div
            role="button"
            tabIndex={0}
            onClick={expandSection}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                expandSection();
              }
            }}
            className="relative z-10 block w-full cursor-pointer overflow-hidden rounded-2xl border border-theme-secondary bg-theme-main text-left shadow-sm"
            aria-label={`Show ${changes.length} changes from ${whenLabel || "this publish"}`}
          >
            <div className="pointer-events-none">
              {renderChange(changes[0])}
            </div>
          </div>
          {peek.length > 0 && (
            <button
              type="button"
              onClick={expandSection}
              className="relative -mt-1 block w-full overflow-hidden text-left"
              style={{ height: peekHeight }}
              aria-label={`Show ${changes.length} changes from ${whenLabel || "this publish"}`}
            >
              {peek.map((change, index) => (
                <span
                  key={change.hash}
                  className="absolute flex items-center overflow-hidden rounded-b-2xl border border-t-0 border-theme-secondary bg-theme-main px-4"
                  style={{
                    top: index * STACK_PEEK,
                    left: (index + 1) * STACK_INSET,
                    right: (index + 1) * STACK_INSET,
                    height: STACK_PEEK,
                    zIndex: peek.length - index,
                  }}
                >
                  <span className="min-w-0 truncate text-xs opacity-50">
                    {change.description || "Untitled change"}
                  </span>
                </span>
              ))}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

const formatPublishWhen = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const HourglassIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6.5 3.75h11M6.5 20.25h11M8 3.75v2.2c0 1.7.9 3.3 2.4 4.15L12 11l1.6-.9A4.8 4.8 0 0 0 16 5.95v-2.2M8 20.25v-2.2c0-1.7.9-3.3 2.4-4.15L12 13l1.6.9a4.8 4.8 0 0 1 2.4 4.15v2.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChangeStatusIcon = ({ status, isActive }) => {
  const tipId = useId();
  let tip = "Queued";
  let icon = <HourglassIcon className="h-3.5 w-3.5 opacity-70" />;
  if (status === "queued") {
    tip = "Queued";
    icon = <HourglassIcon className="h-3.5 w-3.5 opacity-70" />;
  } else if (isActive || status === "running") {
    tip = "Running";
    icon = (
      <span
        className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-primary"
        aria-hidden="true"
      />
    );
  } else if (status === "completed" || status === "published") {
    tip = status === "published" ? "Published" : "Ready";
    icon = <CheckIcon className="h-3.5 w-3.5 text-[#2f7d4f]" aria-hidden="true" />;
  } else if (status === "failed") {
    tip = "Failed";
    icon = <XMarkIcon className="h-3.5 w-3.5 text-[#b3261e]" aria-hidden="true" />;
  } else if (status === "canceled") {
    tip = "Canceled";
    icon = <XMarkIcon className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />;
  }
  return (
    <span
      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center"
      data-tooltip-id={tipId}
      data-tooltip-content={tip}
    >
      {icon}
      <Tooltip id={tipId} place="top" className="z-[100] !text-xs" />
    </span>
  );
};

const ChangeCard = ({
  change,
  onResolve,
  onDeploymentAction,
  onViewRun,
  onChangeUpdated,
  showBuildCost = false,
  showPreview = true,
  canReject = false,
  canDiscard = false,
  canRetry = false,
  listMode = false,
  grouped = false,
  hideDeployments = false,
  startOpen = false,
  className = "",
}) => {
  const project = useSelector((state) => state.project.currentProject);
  const { success, error: toastError } = useToast();
  const isPending = String(change.hash).startsWith(PENDING_PREFIX);
  const [now, setNow] = useState(() => Date.now());
  const [showHiddenRuns, setShowHiddenRuns] = useState(false);
  const [showHiddenDeployments, setShowHiddenDeployments] = useState(false);
  const [autoPublishOpen, setAutoPublishOpen] = useState(false);
  const [savingPreApprove, setSavingPreApprove] = useState(false);
  const autoPublishRef = useRef(null);
  const runs = change.agent_runs || [];
  const deployments = hideDeployments ? [] : (change.deployments || []);
  const hasActiveRun = change.status !== "canceled"
    && runs.some((run) => run.status === "running" || run.status === "pending");
  const activeRun = hasActiveRun
    ? runs.find((run) => run.status === "running" || run.status === "pending")
    : undefined;
  const isActive = ACTIVE_CHANGE_STATUSES.has(change.status) || isPending || hasActiveRun;
  const displayStatus = hasActiveRun && change.status === "failed" ? "running" : change.status;
  const displayStatusMessage = isActive
    ? changeStatusMessage(change, activeRun, hasActiveRun)
    : "";
  const hiddenRuns = runs.filter((run) => isHiddenAgentRun(run, runs, change.status, hasActiveRun));
  const visibleRuns = showHiddenRuns
    ? runs
    : runs.filter((run) => !isHiddenAgentRun(run, runs, change.status, hasActiveRun));
  const runningRun = activeRun;
  const hasActiveDeploy = deployments.some((d) => ACTIVE_DEPLOY_STATUSES.has(d.status));
  const activeDeployment = hasActiveDeploy
    ? deployments.find((d) => ACTIVE_DEPLOY_STATUSES.has(d.status))
    : undefined;
  const hiddenDeployments = deployments.filter(
    (d) => isHiddenDeployment(d, deployments, hasActiveDeploy),
  );
  const visibleDeployments = showHiddenDeployments
    ? deployments
    : deployments.filter((d) => !isHiddenDeployment(d, deployments, hasActiveDeploy));
  const isDeploying = Boolean(activeDeployment);
  const hasSummary = Boolean((change.summary || "").trim());
  const [agentRunsOpen, setAgentRunsOpen] = useState(hasActiveRun);
  const [openRunHash, setOpenRunHash] = useState(() => runningRun?.hash || "");
  const [deploymentsOpen, setDeploymentsOpen] = useState(isDeploying);
  const [openDeploymentHash, setOpenDeploymentHash] = useState(() => activeDeployment?.hash || "");
  const [rowOpen, setRowOpen] = useState(() => !listMode || startOpen);

  useEffect(() => {
    if (!listMode || !startOpen) return;
    setRowOpen(true);
  }, [listMode, startOpen]);

  useEffect(() => {
    if (!isActive) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isActive]);

  useEffect(() => {
    if (!runningRun?.hash) return;
    setRowOpen(true);
    setAgentRunsOpen(true);
    setOpenRunHash(runningRun.hash);
  }, [runningRun?.hash]);

  useEffect(() => {
    if (isDeploying) setDeploymentsOpen(true);
  }, [isDeploying]);

  useEffect(() => {
    if (activeDeployment?.hash) setOpenDeploymentHash(activeDeployment.hash);
  }, [activeDeployment?.hash]);

  const canSetAutoPublish = !isPending && (change.status === "queued" || change.status === "running" || hasActiveRun);

  useEffect(() => {
    if (!autoPublishOpen) return undefined;
    const onDoc = (event) => {
      if (autoPublishRef.current && !autoPublishRef.current.contains(event.target)) {
        setAutoPublishOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [autoPublishOpen]);

  const setPreApprove = (value) => {
    if (!project?.hash || !change?.hash || savingPreApprove) return;
    setSavingPreApprove(true);
    changeRequestsService.update(
      project.hash,
      change.hash,
      { pre_approve: Boolean(value) },
      (response) => {
        setSavingPreApprove(false);
        onChangeUpdated?.(response.data);
      },
      (err) => {
        setSavingPreApprove(false);
        toastError(getApiErrorMessage(err, "Could not update auto-publish."));
      },
    );
  };

  const stageBreakdown = agentRunBreakdown(runs, now, activeDeployment || deployments[deployments.length - 1]);
  const durationLabel = formatTotalDuration(change, now);
  const costTotal = stageBreakdown.reduce((sum, stage) => sum + stage.cost, 0);
  const tokensTotal = stageBreakdown.reduce((sum, stage) => sum + stage.tokens, 0);
  const costLabel = showBuildCost ? formatCost(costTotal) : "";
  const tokensLabel = showBuildCost ? formatTokens(tokensTotal) : "";
  const modelLabel = runs.length <= 1 ? formatModel(runs[0]?.model || runs[0]?.agent_config || change.agent_config) : "";
  const buildProgress = change.agent_type === "sitebuilder" && typeof project?.build_progress === "number"
    ? project.build_progress
    : null;

  const draftPreview = change.status !== "published"
    && change.status !== "deployed"
    && change.environment_status !== "closed"
    && change.environment_status !== "tearing_down";
  const previewRun = draftPreview
    ? [...runs].reverse().find((run) => run.preview_url)
    : null;
  const viewHref = previewRun
    ? sitePath(project, `preview/?env=draft&run=${previewRun.hash}`)
    : draftPreview && change.latest_preview_url
      ? sitePath(project, "preview/?env=draft")
      : null;

  const openLogs = () => {
    const target = runningRun || [...runs].reverse()[0];
    if (!target) return;
    setRowOpen(true);
    setAgentRunsOpen(true);
    setOpenRunHash(target.hash);
  };

  const promptText = change.agent_type === "sitebuilder" && project?.description
    ? project.description
    : change.description;

  const copyPrompt = () => {
    const text = promptText || "";
    navigator.clipboard.writeText(text).then(() => {
      const detail = text.length > 500 ? `${text.slice(0, 500).trimEnd()}…` : text;
      success("Copied to clipboard", 5000, detail ? { detail } : undefined);
    });
  };

  const detailsHidden = listMode && !rowOpen;
  let detailsClass;
  if (listMode) {
    detailsClass = rowOpen
      ? "grid grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out"
      : "grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-out";
  }
  const actionSecondary = ACTION_SECONDARY;
  const actionPrimary = ACTION_PRIMARY;
  const actionIcon = ACTION_ICON;
  const subtitleLinkClass = "inline-flex shrink-0 items-center gap-1 opacity-50 hover:underline hover:opacity-100";
  const timestampLabel = new Date(change.create_date).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const leftMeta = (
    <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-theme-text/50">
      <span className="shrink-0">{timestampLabel}</span>
      {modelLabel && (
        <>
          <span className="shrink-0">·</span>
          <span className="shrink-0">{modelLabel}</span>
        </>
      )}
      {costLabel && (
        <>
          <span className="shrink-0">·</span>
          <HoverStat
            label={costLabel}
            rows={stageBreakdown.map((stage) => ({
              label: stage.label,
              value: formatCost(stage.cost),
              amount: stage.cost,
            }))}
          />
        </>
      )}
      {tokensLabel && (
        <>
          <span className="shrink-0">·</span>
          <HoverStat
            label={tokensLabel}
            rows={stageBreakdown.map((stage) => ({
              label: stage.label,
              value: formatTokens(stage.tokens),
              amount: stage.tokens,
            }))}
          />
        </>
      )}
      {durationLabel && (
        <>
          <span className="shrink-0">·</span>
          <HoverStat
            label={durationLabel}
            rows={stageBreakdown.map((stage) => ({
              label: stage.label,
              value: formatDurationSeconds(stage.seconds),
              amount: stage.seconds,
            }))}
          />
        </>
      )}
      {buildProgress != null && (
        <>
          <span className="shrink-0">·</span>
          <span className="shrink-0 tabular-nums">{buildProgress}%</span>
        </>
      )}
      {isActive && displayStatusMessage && (
        <>
          <span className="shrink-0">·</span>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              openLogs();
            }}
            className="inline-flex min-w-0 items-baseline text-left hover:opacity-80"
          >
            <span className="truncate">{displayStatusMessage}</span>
            <AnimatedDots />
          </button>
        </>
      )}
      {showPreview && !isPending && previewRun && onViewRun && (
        <>
          <span className="shrink-0">·</span>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onViewRun(previewRun.preview_url, previewRun.hash);
            }}
            className={subtitleLinkClass}
          >
            <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Preview
          </button>
        </>
      )}
      {showPreview && !isPending && viewHref && !onViewRun && (
        <>
          <span className="shrink-0">·</span>
          <Link
            to={viewHref}
            onClick={(event) => event.stopPropagation()}
            className={subtitleLinkClass}
          >
            <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Preview
          </Link>
        </>
      )}
    </span>
  );

  const rightMeta = !isPending && onResolve && canReject && change.status === "completed" ? (
    <span className="flex shrink-0 items-center gap-2.5 text-xs">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (!window.confirm("Undo this change? It will be discarded and the draft preview will revert.")) return;
          onResolve(change, "reject");
        }}
        className={subtitleLinkClass}
      >
        <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
        Undo
      </button>
    </span>
  ) : null;

  const headerActions = !isPending && (
    <span className="inline-flex shrink-0 items-center gap-0.5 self-start">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          copyPrompt();
        }}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded opacity-40 hover:opacity-100"
        title="Copy prompt"
      >
        <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      {canSetAutoPublish && (
        <div className="relative inline-flex" ref={autoPublishRef}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setAutoPublishOpen((open) => !open);
            }}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded opacity-40 hover:opacity-100"
            title="Auto-publish"
            aria-label="Auto-publish"
          >
            <Cog6ToothIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          {autoPublishOpen && (
            <div
              className="absolute right-0 top-full z-30 mt-1 w-56 rounded-lg border border-theme-secondary bg-theme-main p-3 shadow-lg"
              onClick={(event) => event.stopPropagation()}
            >
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(change.pre_approve)}
                  disabled={savingPreApprove}
                  onChange={(e) => setPreApprove(e.target.checked)}
                />
                <span>
                  <span className="font-semibold">Auto-publish</span>
                  <span className="mt-0.5 block opacity-60">
                    Publish the draft when this change finishes.
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>
      )}
    </span>
  );

  const stepsLink = runs.length > 0 && (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        setRowOpen(true);
        setAgentRunsOpen(true);
      }}
      className="inline-flex items-center gap-1.5 pt-3 text-xs font-semibold opacity-50 hover:opacity-100"
    >
      <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {runs.length === 1 ? "1 step" : `${runs.length} steps`}
    </button>
  );

  const stepsList = runs.length > 0 && (
    <div className="space-y-2 pt-3">
      <div className="overflow-hidden rounded-lg border border-theme-secondary">
        {visibleRuns.map((run, index) => (
          <AgentRunRow
            key={run.hash || index}
            run={run}
            expanded={openRunHash === run.hash}
            onToggle={() => setOpenRunHash(openRunHash === run.hash ? "" : run.hash)}
            now={now}
            onViewRun={onViewRun}
            allowPreview={draftPreview}
            showBuildCost={showBuildCost}
          />
        ))}
      </div>
      {hiddenRuns.length > 0 && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowHiddenRuns((value) => !value);
          }}
          className="text-xs opacity-50 hover:opacity-80"
        >
          {showHiddenRuns
            ? `Hide ${hiddenRunsKind(hiddenRuns)}`
            : `Show ${hiddenRuns.length} ${hiddenRunsKind(hiddenRuns)}`}
        </button>
      )}
    </div>
  );

  const deploymentsToggle = deployments.length > 0 && (
    <div className="flex items-center gap-2 pt-3">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setDeploymentsOpen((value) => !value);
        }}
        className="inline-flex items-center gap-1.5 text-xs opacity-50 hover:opacity-100"
      >
        <ChevronRightIcon
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${deploymentsOpen ? "rotate-90" : ""}`}
        />
        <span className="font-semibold">
          {deployments.length === 1 ? "1 deployment" : `${deployments.length} deployments`}
        </span>
      </button>
      {deploymentsOpen && hiddenDeployments.length > 0 && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setShowHiddenDeployments((value) => !value);
          }}
          className="text-xs opacity-50 hover:opacity-80"
        >
          {showHiddenDeployments
            ? `Hide ${hiddenDeploymentsKind(hiddenDeployments)}`
            : `Show ${hiddenDeployments.length} ${hiddenDeploymentsKind(hiddenDeployments)}`}
        </button>
      )}
    </div>
  );

  const deploymentsList = deployments.length > 0 && deploymentsOpen && (
    <div className="overflow-hidden rounded-lg border border-theme-secondary">
      {visibleDeployments.map((deployment, index) => (
        <DeploymentRow
          key={deployment.hash || index}
          deployment={deployment}
          expanded={openDeploymentHash === deployment.hash}
          onToggle={() => setOpenDeploymentHash(
            openDeploymentHash === deployment.hash ? "" : deployment.hash,
          )}
          onAction={onDeploymentAction}
        />
      ))}
    </div>
  );

  const collapsed = listMode && !rowOpen;

  const showCancel = !isPending && onResolve && (change.status === "queued" || change.status === "running" || hasActiveRun);
  const showDiscard = !isPending && onResolve && canDiscard && !hasActiveRun && (displayStatus === "failed" || displayStatus === "canceled");
  const showRetry = !isPending && onResolve && canRetry && !hasActiveRun && (displayStatus === "failed" || displayStatus === "canceled");
  const actionButtons = (showCancel || showDiscard || showRetry) ? (
    <div className={`flex shrink-0 items-center justify-end ${listMode ? "gap-1.5" : "flex-wrap gap-2"}`}>
      {showCancel && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onResolve(change, "cancel");
          }}
          className={actionSecondary}
        >
          <NoSymbolIcon className={actionIcon} aria-hidden="true" />
          Cancel
        </button>
      )}
      {showDiscard && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            if (!window.confirm("Discard this change? It will be removed and can't be published.")) return;
            onResolve(change, "reject");
          }}
          className={actionSecondary}
        >
          <TrashIcon className={actionIcon} aria-hidden="true" />
          Discard
        </button>
      )}
      {showRetry && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onResolve(change, "retry");
          }}
          className={actionPrimary}
        >
          <ArrowPathIcon className={actionIcon} aria-hidden="true" />
          Retry
        </button>
      )}
    </div>
  ) : null;

  return (
    <article
      className={`${grouped ? "bg-theme-main" : `border border-theme-secondary bg-theme-main ${listMode ? "overflow-hidden rounded-2xl" : "rounded-xl"}`} ${collapsed ? "cursor-pointer hover:bg-theme-secondary/30" : ""} ${className}`}
      onClick={collapsed ? () => setRowOpen(true) : undefined}
    >
      {listMode ? (
        <div className="space-y-1.5 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <ChangeStatusIcon status={displayStatus} isActive={isActive} />
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setRowOpen((value) => !value);
                }}
                className="min-w-0 truncate text-left font-semibold"
              >
                {promptText}
              </button>
              {headerActions}
            </div>
            {actionButtons}
          </div>
          <div className={`flex items-start gap-3 ${rightMeta ? "justify-between" : ""} ${detailsHidden ? "" : "mb-2"}`}>
            {leftMeta}
            {rightMeta}
          </div>
          {hasSummary && !isActive && !detailsHidden && (
            <SummaryText text={change.summary} className="text-sm opacity-70" />
          )}
          {!detailsHidden && (agentRunsOpen ? stepsList : stepsLink)}
          {!detailsHidden && deploymentsToggle}
          {!detailsHidden && deploymentsList}
        </div>
      ) : (
        <div className="space-y-2 p-5">
          <div className={`flex items-start gap-4 ${actionButtons ? "justify-between" : ""}`}>
            <div className="flex min-w-0 flex-1 items-start gap-2.5">
              <ChangeStatusIcon status={displayStatus} isActive={isActive} />
              <div className="flex min-w-0 flex-1 items-start gap-1.5">
                <p className="min-w-0 line-clamp-2 font-semibold">{promptText}</p>
                {headerActions}
              </div>
            </div>
            {actionButtons}
          </div>
          <div className={`mb-2 flex items-start gap-3 ${rightMeta ? "justify-between" : ""}`}>
            {leftMeta}
            {rightMeta}
          </div>
          {hasSummary && !isActive && (
            <SummaryText text={change.summary} className="text-sm opacity-70" />
          )}
          {agentRunsOpen ? stepsList : stepsLink}
          {deploymentsToggle}
          {deploymentsList}
        </div>
      )}

      <div className={detailsClass}>
        <div className={listMode ? "min-h-0 overflow-hidden" : undefined}>
      {runs.length === 0 && change.agent_log?.length > 0 && (
        <div className="px-4 pb-3">
          <AgentLog lines={change.agent_log} live={isActive} className="mt-1" />
        </div>
      )}
        </div>
      </div>
    </article>
  );
};

const HoverStat = ({ label, rows }) => {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const items = (rows || []).filter((row) => row.value);
  const maxAmount = Math.max(0, ...items.map((row) => Number(row.amount) || 0));
  if (!label) return null;

  const show = () => {
    if (items.length <= 1 || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
    setOpen(true);
  };

  return (
    <span
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
    >
      <span className={items.length > 1 ? "cursor-default underline decoration-dotted decoration-theme-secondary underline-offset-2" : undefined}>
        {label}
      </span>
      {open && items.length > 1 && createPortal(
        <span
          className="pointer-events-none fixed z-[200] min-w-[12rem] rounded-lg border border-theme-secondary bg-theme-main p-1.5 text-left text-[11px] text-theme-text shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          {items.map((row) => {
            const width = maxAmount ? Math.max(8, ((Number(row.amount) || 0) / maxAmount) * 100) : 0;
            return (
              <span key={row.label} className="relative flex justify-between gap-4 overflow-hidden rounded px-2 py-1">
                <span
                  className="absolute inset-y-0.5 left-0 rounded-sm bg-theme-secondary"
                  style={{ width: `${width}%` }}
                />
                <span className="relative text-theme-text/60">{row.label}</span>
                <span className="relative">{row.value}</span>
              </span>
            );
          })}
        </span>,
        document.body,
      )}
    </span>
  );
};

const agentRunBreakdown = (runs, now, deployment) => {
  const stages = [];
  const byLabel = {};
  for (const run of runs) {
    const label = formatRunLabel(run.run_label, AGENT_LABELS_LONG[run.agent_type] || "run");
    if (!byLabel[label]) {
      byLabel[label] = { label, seconds: 0, tokens: 0, cost: 0 };
      stages.push(byLabel[label]);
    }
    const isRunning = run.status === "running" || run.status === "pending";
    byLabel[label].seconds += runDurationSeconds(
      {
        start: run.started_at || run.create_date,
        end: run.completed_at,
        running: isRunning,
      },
      now,
    );
    const usage = run.usage || {};
    byLabel[label].tokens += (usage.tokens_sent || 0) + (usage.tokens_received || 0);
    byLabel[label].cost += Number(run.cost_usd || usage.cost_usd || 0);
  }
  if (deployment) {
    const deploying = ACTIVE_DEPLOY_STATUSES.has(deployment.status);
    const seconds = runDurationSeconds(
      {
        start: deployment.create_date,
        end: deployment.resolved_at,
        running: deploying,
      },
      now,
    );
    if (seconds || deploying) {
      stages.push({ label: "Deployment", seconds, tokens: 0, cost: 0 });
    }
  }
  return stages;
};

const StageStatusIcon = ({
  status,
  title = "",
  previewUrl = "",
  onPreview = null,
  previewHref = "",
}) => {
  const tooltipId = useId();
  const canPreview = status === "succeeded" && Boolean(previewUrl) && (onPreview || previewHref);
  let icon = null;
  if (status === "running" || status === "pending") {
    icon = (
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-primary" />
    );
  } else if (canPreview) {
    icon = <EyeIcon className="h-3 w-3 text-[#2f7d4f]" />;
  } else if (status === "succeeded") {
    icon = <CheckIcon className="h-3 w-3 text-[#2f7d4f]" />;
  } else if (status === "failed") {
    icon = <XMarkIcon className="h-3 w-3 text-[#b3261e]" />;
  } else if (status === "canceled") {
    icon = <XMarkIcon className="h-3 w-3 text-theme-text opacity-40" />;
  }
  const tip = canPreview ? (title ? `${title} · Preview` : "Preview") : title;
  const wrapClass = "inline-flex h-4 w-4 shrink-0 items-center justify-center";
  const tipProps = tip
    ? { "data-tooltip-id": tooltipId, "data-tooltip-content": tip }
    : { "aria-hidden": "true" };
  let body = (
    <span className={wrapClass} {...tipProps}>
      {icon}
    </span>
  );
  if (canPreview && onPreview) {
    body = (
      <button type="button" onClick={onPreview} className={`${wrapClass} hover:opacity-80`} {...tipProps}>
        {icon}
        <span className="sr-only">Preview</span>
      </button>
    );
  } else if (canPreview && previewHref) {
    body = (
      <Link to={previewHref} className={`${wrapClass} hover:opacity-80`} {...tipProps}>
        {icon}
        <span className="sr-only">Preview</span>
      </Link>
    );
  }
  return (
    <>
      {body}
      {tip && (
        <Tooltip
          id={tooltipId}
          place="left"
          className="z-50 !max-w-xs !text-left !text-xs !leading-snug"
        />
      )}
    </>
  );
};

const AgentRunRow = ({ run, expanded, onToggle, now, onViewRun, allowPreview = true, showBuildCost = false }) => {
  const project = useSelector((state) => state.project.currentProject);
  const isRunning = run.status === "running" || run.status === "pending";
  const timestampLabel = formatAgentRunTimestamp(run);
  const header = formatRunLabel(run.run_label, AGENT_LABELS_LONG[run.agent_type] || "Agent run");
  const canPreview = allowPreview && run.status === "succeeded" && Boolean(run.preview_url);
  const dataDiff =
    run.agent_type === "data" || run.agent_type === "media"
      ? run.agent_config?.data_diff
      : null;
  const mediaDiff = run.agent_config?.media_diff;
  const fromSha = dataDiff?.from_sha || "main";
  const toSha = dataDiff?.to_sha || run.github_sha;
  const firstDiffTable = dataDiff?.tables?.[0]?.name || "";
  const showDataDiff = Boolean(
    project
    && toSha
    && dataDiff
    && (
      dataDiff.cells_added
      || dataDiff.cells_deleted
      || dataDiff.cells_updated
      || (dataDiff.tables || []).length
    ),
  );
  const dataDiffHref = showDataDiff
    ? sitePath(
      project,
      `data/${firstDiffTable ? `${firstDiffTable}/` : ""}?from=${encodeURIComponent(fromSha)}&to=${encodeURIComponent(toSha)}`,
    )
    : "";
  const mediaHref = project && (mediaDiff?.images_added || mediaDiff?.images_edited)
    ? sitePath(project, `media/?agent_run=${encodeURIComponent(run.hash)}`)
    : "";
  const durationLabel = formatDurationSeconds(runDurationSeconds({
    start: run.started_at || run.create_date,
    end: run.completed_at,
    running: isRunning,
  }, now), { running: false });
  const logMeta = [
    formatModel(run.model || run.agent_config),
    showBuildCost ? formatCost(Number(run.cost_usd || run.usage?.cost_usd || 0)) : "",
    durationLabel,
  ].filter(Boolean).join(" · ");

  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 items-center gap-2 text-left text-xs hover:opacity-80"
          >
            <ChevronRightIcon
              className={`h-3.5 w-3.5 shrink-0 opacity-50 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
            <span className="truncate opacity-80">{header}</span>
            {durationLabel && (
              <span className="shrink-0 opacity-40">{durationLabel}</span>
            )}
          </button>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-xs">
          {dataDiffHref && (
            <Link
              to={dataDiffHref}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold hover:opacity-80"
              title="View data diff"
            >
              <span className="text-emerald-600">+{dataDiff.cells_added || 0}</span>
              <span className="text-red-500">-{dataDiff.cells_deleted || 0}</span>
              <span className="text-amber-600">~{dataDiff.cells_updated || 0}</span>
            </Link>
          )}
          {mediaHref && (
            <Link
              to={mediaHref}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold text-theme-text hover:opacity-80"
              title="View media from this run"
            >
              {(mediaDiff.images_added || 0) > 0 && (
                <span className="text-emerald-600">+{mediaDiff.images_added}</span>
              )}
              {(mediaDiff.images_edited || 0) > 0 && (
                <span className="text-amber-600">~{mediaDiff.images_edited}</span>
              )}
              <PhotoIcon className="h-3 w-3 shrink-0 text-emerald-600" aria-hidden="true" />
            </Link>
          )}
          {timestampLabel && (
            <span className="shrink-0 opacity-50">{timestampLabel}</span>
          )}
          <StageStatusIcon
            status={run.status}
            title={run.summary || ""}
            previewUrl={canPreview ? run.preview_url : ""}
            onPreview={canPreview && onViewRun ? () => onViewRun(run.preview_url, run.hash) : null}
            previewHref={canPreview && !onViewRun ? sitePath(project, `preview/?env=draft&run=${run.hash}`) : ""}
          />
        </div>
      </div>
      {expanded && (
        <AgentLog
          lines={run.agent_log || []}
          live={isRunning}
          meta={logMeta}
          className="border-t border-theme-secondary"
        />
      )}
    </div>
  );
};

const DeploymentRow = ({ deployment, expanded, onToggle, onAction }) => {
  const isActive = ACTIVE_DEPLOY_STATUSES.has(deployment.status);
  const durationLabel = deployment.resolved_at
    ? formatDeployDuration(deployment.create_date, deployment.resolved_at)
    : "";
  const iconStatus = deployment.status === "building"
    ? "running"
    : deployment.status === "live"
      ? "succeeded"
      : deployment.status;

  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-xs hover:opacity-80"
        >
          <ChevronRightIcon
            className={`h-3.5 w-3.5 shrink-0 opacity-50 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
          <span className="truncate opacity-80">Deployment v{deployment.version}</span>
          {durationLabel && (
            <span className="shrink-0 opacity-40">{durationLabel}</span>
          )}
        </button>
        <div className="flex shrink-0 items-center gap-2 text-xs">
          {deployment.status !== "live" && (
            <StatusBadge status={deployment.status} />
          )}
          <StageStatusIcon status={iconStatus} title={deployment.status_message || ""} />
        </div>
      </div>
      {expanded && (
        <div className="space-y-3 border-t border-theme-secondary px-3 pb-3 pt-2">
          {deployment.status !== "live" && deployment.status_message && (
            <p className="flex items-center gap-2 text-sm opacity-60">
              {isActive && (
                <span className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-primary" />
              )}
              {deployment.status_message}
            </p>
          )}
          {deployment.deploy_log?.length > 0 && (
            <AgentLog lines={deployment.deploy_log} live={isActive} compact />
          )}
          {onAction && (
            <div className="flex flex-wrap justify-end gap-2">
              {(deployment.status === "failed" || deployment.status === "building") && (
                <button
                  type="button"
                  onClick={() => onAction(deployment, "cancel")}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-1.5 text-xs font-semibold"
                >
                  <NoSymbolIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Cancel
                </button>
              )}
              {deployment.status !== "live" && deployment.status !== "building" && deployment.status !== "failed" && deployment.status !== "canceled" && (
                <button
                  type="button"
                  onClick={() => onAction(deployment, "rollback")}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-1.5 text-xs font-semibold"
                >
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Rollback
                </button>
              )}
              {(deployment.status === "failed" || deployment.status === "canceled") && (
                <button
                  type="button"
                  onClick={() => onAction(deployment, "redeploy")}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-theme-primary px-3 py-1.5 text-xs font-semibold text-theme-primary"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Retry deploy
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formatAgentRunTimestamp = (run) => {
  const isRunning = run.status === "running" || run.status === "pending";
  const value = run.completed_at || (isRunning ? (run.started_at || run.create_date) : "");
  if (!value) return "";
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const stoppedChange = (change) => {
  const hasActiveRun = (change.agent_runs || []).some(
    (run) => run.status === "running" || run.status === "pending",
  );
  const keepFailed = change.status === "failed" && hasActiveRun;
  return {
    ...change,
    status: keepFailed ? change.status : "canceled",
    status_message: keepFailed ? change.status_message : "Stopped",
    agent_runs: (change.agent_runs || []).map((run) => (
      run.status === "running" || run.status === "pending"
        ? { ...run, status: "canceled", status_message: "Stopped by user" }
        : run
    )),
  };
};

const rejectedChange = (change) => ({
  ...change,
  status: "rejected",
  status_message: "Change rejected",
});

const changeStatusMessage = (change, activeRun, hasActiveRun) => {
  if (activeRun?.status_message) return activeRun.status_message;
  if (hasActiveRun && isStaleFailureMessage(change.status_message)) return "";
  return change.status_message || "";
};

const isStaleFailureMessage = (message) => {
  if (!message) return false;
  return /^(site build failed|failed:)/i.test(message);
};

const isHiddenDeployment = (deployment, deployments, hasActiveDeploy) => {
  if (!["failed", "canceled", "superseded"].includes(deployment.status)) return false;
  const index = deployments.indexOf(deployment);
  if (deployments.some((other, otherIndex) => otherIndex > index)) return true;
  if (hasActiveDeploy) return true;
  return false;
};

const hiddenDeploymentsKind = (hiddenDeployments) => {
  const canceled = hiddenDeployments.some((d) => d.status === "canceled");
  const failed = hiddenDeployments.some((d) => d.status === "failed");
  if (canceled && failed) return "canceled/failed";
  if (failed) return "failed";
  return "canceled";
};

const isHiddenAgentRun = (run, runs, changeStatus, hasActiveRun) => {
  if (run.status !== "failed" && run.status !== "canceled") return false;

  const label = run.run_label || "";
  const index = runs.indexOf(run);

  if (runs.some((other, otherIndex) => otherIndex > index && (other.run_label || "") === label)) {
    return true;
  }

  if (runs.some((other) => other !== run && (other.run_label || "") === label && other.status === "succeeded")) {
    return true;
  }

  if (hasActiveRun) return true;

  return changeStatus !== "failed" && changeStatus !== "canceled";
};

const hiddenRunsKind = (hiddenRuns) => {
  const canceled = hiddenRuns.some((run) => run.status === "canceled");
  const failed = hiddenRuns.some((run) => run.status === "failed");
  if (canceled && failed) return "canceled/failed";
  if (failed) return "failed";
  return "canceled";
};

const formatDeployDuration = (createDate, resolvedAt) => {
  const seconds = Math.round((new Date(resolvedAt) - new Date(createDate)) / 1000);
  if (seconds < 1) return "<1s";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const ChangeComposer = ({
  description,
  modelOverride,
  onChange,
  onModelChange,
  onSubmit,
  autoFocus = false,
  placeholder = "Describe a change...",
  disabled = false,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 72), 220)}px`;
  }, [description]);

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-theme-shadow bg-theme-main shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_40px_-18px_rgba(0,0,0,0.35)] transition-shadow duration-300 focus-within:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_48px_-16px_rgba(0,0,0,0.28)]"
    >
      <textarea
        ref={ref}
        value={description}
        autoFocus={autoFocus}
        disabled={disabled}
        rows={2}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            if (description.trim() && !disabled) onSubmit(event);
          }
        }}
        className="block max-h-56 min-h-[72px] w-full resize-none rounded-t-2xl border-0 bg-transparent px-4 pt-4 text-[15px] leading-6 outline-none ring-0 placeholder:text-theme-secondary-text focus:ring-0 disabled:opacity-60"
      />
      <div className="flex items-center justify-between gap-3 px-3 pb-3">
        <div className="flex min-w-0 items-center gap-3">
          <PremiumModelPicker compact value={modelOverride || ""} onChange={onModelChange} />
          <span className="hidden text-[11px] text-theme-secondary-text sm:inline">Enter to send</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <VoiceInputButton value={description} onChange={onChange} disabled={disabled} />
          <button
            type="submit"
            disabled={disabled || !description.trim()}
            aria-label="Submit change"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-theme-primary text-theme-button transition hover:opacity-90"
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </form>
  );
};

const promptFromSuggestions = (items) => {
  if (items.length === 1) return items[0].prompt;

  const groups = [];
  const byCode = new Map();
  items.forEach((item) => {
    const key = item.check_code || item.hash;
    if (!byCode.has(key)) {
      const group = { key, items: [] };
      byCode.set(key, group);
      groups.push(group);
    }
    byCode.get(key).items.push(item);
  });

  const lines = ["Fix all of the following SEO issues in one pass."];
  let n = 1;
  groups.forEach((group) => {
    const sample = group.items[0];
    const label = sample.check_label || sample.title;
    lines.push("");
    if (sample.help) {
      lines.push(`${label}: ${sample.help}`);
      lines.push("");
    } else if (label) {
      lines.push(`${label}:`);
      lines.push("");
    }
    group.items.forEach((item) => {
      // First paragraph is the page-specific finding; skip repeated help baked into older prompts.
      const finding = (item.prompt || "").split(/\n\n/)[0] || item.title;
      lines.push(`${n}. ${finding}`);
      n += 1;
    });
  });
  lines.push("");
  lines.push(
    "Update the Astro templates and page copy as needed so each issue above is resolved.",
  );
  return lines.join("\n");
};

const SUMMARY_COLLAPSE_CHARS = 220;

function AnimatedDots() {
  const frames = [".", "..", "...", ""];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % frames.length), 400);
    return () => clearInterval(id);
  }, []);
  return (
    <span aria-hidden="true" className="inline-block w-4 shrink-0 text-left">
      {frames[step]}
    </span>
  );
}

const ChangeSummary = ({ text, expanded, onExpand, className = "" }) => {
  const summary = (text || "").trim();
  if (!summary) return null;
  const needsCollapse = summary.length > SUMMARY_COLLAPSE_CHARS || summary.includes("\n");
  if (expanded || !needsCollapse) {
    return <SummaryText text={summary} className={className} />;
  }
  return (
    <button
      type="button"
      onClick={onExpand}
      className={`group w-full text-left hover:opacity-100 ${className}`}
      title="Show full summary"
    >
      <SummaryText
        text={summary}
        className="line-clamp-3 group-hover:underline"
      />
    </button>
  );
};

export { ChangeCard, ChangeComposer, ChangeSummary, StageStatusIcon, stoppedChange, rejectedChange, isHiddenAgentRun, isHiddenDeployment };
export default Changes;
